import {HttpClient} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {BehaviorSubject, Observable, tap, map} from "rxjs";
import {LoaderService} from "./loader.service";
import {
  ActionResult,
  ActionResultList, CompetitorCount,
  EventItem, EventStatus, FetchEventByIdRequest, FindEventsRequest,
  Lookups, Race, RaceStatus
} from "@model";
import {EnvironmentService} from "./environment.service";

@Injectable({ providedIn:'root' })
export class EventService {
  private static _events: BehaviorSubject<ActionResultList<(EventItem | Race)[], number> | undefined> = new BehaviorSubject<ActionResultList<(EventItem | Race)[], number> | undefined>(undefined);
  private static _favoriteEvents: BehaviorSubject<ActionResultList<EventItem[], number> | null> = new BehaviorSubject<ActionResultList<EventItem[], number> | null>(null);
  private static _currentEvent: BehaviorSubject<EventItem | undefined> = new BehaviorSubject<EventItem | undefined>(undefined);


  get events(): BehaviorSubject<ActionResultList<(EventItem | Race)[], number> | undefined> {
    return EventService._events;
  }

  get favoriteEvents(): BehaviorSubject<ActionResultList<EventItem[], number> | null> {
    return EventService._favoriteEvents;
  }

  get currentEvent(): BehaviorSubject<EventItem | undefined> {
    return EventService._currentEvent;
  }

  constructor(private http: HttpClient, private loaderService: LoaderService, private environmentService:EnvironmentService) {
  }


  setCurrentEvent(eventItem: EventItem | undefined) {
    this.currentEvent.next(eventItem);
  }

  getFilteredEventStatuses(eventId?: number): Observable<Lookups[] | []> {
    return this.http.post<ActionResult<Lookups[]>>(this.environmentService.getApiUrl() + "/api/public/lookups/statusesfiltered", eventId).pipe(
      map((value: ActionResult<Lookups[]>) => {
        if (value && value.result && value.result.length != 0) {
          return value.result;
        } else {
          return [];
        }
      })
    );
  }

  findEventById(eventId: FetchEventByIdRequest, hideLoading?: boolean): Observable<EventItem | null> {
    if (!hideLoading) {
      this.loaderService.startLoading();
    }
    return this.http.post<ActionResult<EventItem>>(this.environmentService.getApiUrl() + "/api/public/events/geteventbyid", eventId)
      .pipe(
        tap(() => {
          if (!hideLoading) {
            this.loaderService.dismissLoading()
          }
        }),
        map((value: ActionResult<EventItem>) => {
          if (value && value.result) {
            this.currentEvent.next(value.result);
            return value.result;
          } else {
            this.currentEvent.next(undefined);
            return null;
          }
        })
      );
  }

  removeEventFromFavorites(eventId: number | undefined): Observable<ActionResult<boolean>> {
    let endpoint: string = "/api/events/favorite/remove";
    return this.http.post<ActionResult<boolean>>(this.environmentService.getApiUrl() + endpoint, eventId);
  }

  addEventToFavorites(eventId: number): Observable<ActionResult<boolean>> {
    let endpoint: string = "/api/events/favorite/add";
    return this.http.post<ActionResult<boolean>>(this.environmentService.getApiUrl() + endpoint, eventId)
  }
  fetchUserFavoritesEvent(): Observable<EventItem[]> {
    return this.http.get<ActionResultList<(EventItem)[], number>>(this.environmentService.getApiUrl() + "/api/events/favorite/events").pipe(map((value: ActionResultList<EventItem[], number>) => {
        if (value && value.result && value.result.length != 0) {
          this.favoriteEvents.next(value);
          return value.result;
        } else {
          this.favoriteEvents.next(null);
          return [];
        }
      })
    );
  }


  findEventByIdProtected(eventId: FetchEventByIdRequest, hideLoading?: boolean): Observable<EventItem | null> {
    if (!hideLoading) {
      this.loaderService.startLoading();
    }
    return this.http.post<ActionResult<EventItem>>(this.environmentService.getApiUrl() + "/api/events/geteventbyid", eventId)
      .pipe(
        tap(() => {
          if (!hideLoading) {
            this.loaderService.dismissLoading()
          }
        }),
        map((value: ActionResult<EventItem>) => {
          if (value && value.result) {
            this.currentEvent.next(value.result);
            return value.result;
          } else {
            this.currentEvent.next(undefined);
            return null;
          }
        })
      );
  }

  fetchEventToCopy(eventId: FetchEventByIdRequest, hideLoading?: boolean): Observable<EventItem | null> {
    if (!hideLoading) {
      this.loaderService.startLoading();
    }
    return this.http.post<ActionResult<EventItem>>(this.environmentService.getApiUrl() + "/api/events/geteventbyid", eventId)
      .pipe(
        tap(() => {
          if (!hideLoading) {
            this.loaderService.dismissLoading()
          }
        }),
        map((value: ActionResult<EventItem>) => {
          if (value && value.result) {
            let eventItem = value.result;
            eventItem.status = EventStatus.INACTIVE;
            eventItem.name = undefined;
            eventItem.nameEng = undefined;
            eventItem.nameIta = undefined;
            eventItem.nameSlo = undefined;
            eventItem.start = new Date();
            eventItem.end = new Date();
            eventItem.racesList?.forEach((race: Race) => {
              race.status = RaceStatus.INACTIVE
              race.raceStart = undefined;
              race.raceEnd = undefined;
              race.registrationEnd = undefined;
              race.registrationStart = undefined;
              race.competitorCount = new CompetitorCount();
              race.competitorCount.total = 0;
            });
            return eventItem;
          } else {
            return null;
          }
        })
      );
  }

  findEventListByName(searchFilter: FindEventsRequest): Observable<ActionResultList<EventItem[], number>> {
    return this.http.post<ActionResultList<EventItem[], number>>(this.environmentService.getApiUrl() + "/api/public/events/active/byName", searchFilter);
  }

  findEventCalendarList(filter: FindEventsRequest): Observable<EventItem[]> {
    return this.http.post<ActionResultList<(EventItem)[], number>>(this.environmentService.getApiUrl() + "/api/public/events/calendar/filter", filter).pipe(
      map((value: ActionResultList<EventItem[], number>) => {
        if (value && value.result && value.result.length != 0) {
          return value.result;
        } else {
          return [];
        }
      })
    );
  }

  filterFinishedEventsList(filter: FindEventsRequest): Observable<EventItem[]> {
    return this.http.post<ActionResultList<(EventItem)[], number>>(this.environmentService.getApiUrl() + "/api/public/events/finished/filter", filter).pipe(
      map((value: ActionResultList<EventItem[], number>) => {
        if (value && value.result && value.result.length != 0) {
          return value.result;
        } else {
          return [];
        }
      })
    );
  }

  findUpcomingEventsList(): Observable<EventItem[]> {
    return this.http.get<ActionResultList<(EventItem)[], number>>(this.environmentService.getApiUrl() + "/api/public/events/landing/upcoming").pipe(map((value: ActionResultList<EventItem[], number>) => {
        if (value && value.result && value.result.length != 0) {
          return value.result;
        } else {
          return [];
        }
      })
    );
  }

  findNewEventsList(): Observable<EventItem[]> {
    return this.http.get<ActionResultList<(EventItem)[], number>>(this.environmentService.getApiUrl() + "/api/public/events/landing/new").pipe(map((value: ActionResultList<EventItem[], number>) => {
        if (value && value.result && value.result.length != 0) {
          return value.result;
        } else {
          return [];
        }
      })
    );
  }

  fetchAdminEvents(eventFilter: FindEventsRequest): Observable<EventItem[]> {
    return this.http.post<ActionResultList<(EventItem)[], number>>(this.environmentService.getApiUrl() + "/api/events/organiserevents", eventFilter).pipe(
      map((value: ActionResultList<EventItem[], number>) => {
        if (value && value.result && value.result.length != 0) {
          return value.result;
        } else {
          return [];
        }
      })
    );
  }


  updateEvent(eventItem: EventItem, onSuccess: () => void, onError: () => void): Observable<EventItem | null> {
    this.loaderService.startLoading();
    return this.http.post<ActionResult<EventItem>>(this.environmentService.getApiUrl() + (eventItem.id ? "/api/events/update" : "/api/events/add"), eventItem)
      .pipe(
        tap(() => {
          this.loaderService.dismissLoading();
        }),
        map((value: ActionResult<EventItem>) => {
          if (value && value.result) {
            onSuccess();
            return value.result;
          } else {
            onError();
            return null;
          }
        })
      );
  }

  deleteEvent(event: EventItem, onSuccess: () => void, onError: () => void): void {
    this.loaderService.startLoading();
    this.http.post<ActionResult<boolean>>
    (this.environmentService.getApiUrl() + "/api/events/delete", event.id)
      .subscribe((actionResult: ActionResult<boolean>) => {
        if (actionResult.resultCode !== -1) {
          this.loaderService.dismissLoading();
          onSuccess();
        } else {
          this.loaderService.dismissLoading();
          onError();
        }
      });
  }

  copyEvent(eventItem: EventItem, onSuccess: () => void, onError: () => void): Observable<EventItem | null> {
    this.loaderService.startLoading();
    return this.http.post<ActionResult<EventItem>>(this.environmentService.getApiUrl() + "/api/copy/event", eventItem)
      .pipe(
        tap(() => {
          this.loaderService.dismissLoading();
        }),
        map((value: ActionResult<EventItem>) => {
          if (value && value.result) {
            onSuccess();
            return value.result;
          } else {
            onError();
            return null;
          }
        })
      );
  }
}

